import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { TrainerShell } from '../components/TrainerShell';
import { unitsLabel } from '../lib/sessionUnits';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const zl = (n) => (n == null ? '—' : `${Math.round(n).toLocaleString('pl-PL')} zł`);
const Card = ({ className = '', children, ...rest }) => <section className={`glass rounded-[28px] ${className}`} {...rest}>{children}</section>;
const Label = ({ children }) => <p className="label-text text-[var(--text-muted)]">{children}</p>;
const weekLabel = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  const end = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 6);
  const f = (x) => `${x.getDate()}.${String(x.getMonth() + 1).padStart(2, '0')}`;
  return `${f(d)}–${f(end)}`;
};

const Kpi = ({ label, value, sub, testid }) => (
  <Card className="p-5 sm:p-6 flex flex-col gap-2.5" data-testid={testid}>
    <Label>{label}</Label>
    <div className="big-num text-3xl sm:text-4xl">{value}</div>
    {sub && <p className="text-xs text-[var(--text-muted)]">{sub}</p>}
  </Card>
);

// Earned (solid) + still planned this week (light) per week, with the
// weekly average as a dashed line.
const WeekBars = ({ weeks, avg }) => {
  const max = Math.max(1, avg, ...weeks.map((w) => w.earned + w.planned));
  const H = 180;
  return (
    <div className="flex flex-col gap-2">
      <div className="relative flex items-end gap-2 sm:gap-3" style={{ height: H }}>
        {avg > 0 && (
          <div className="absolute left-0 right-0 border-t border-dashed border-[#C9A84C]/70 pointer-events-none" style={{ bottom: (avg / max) * H }}>
            <span className="absolute right-0 -top-5 text-[10px] text-[#C9A84C]">średnio {zl(avg)}</span>
          </div>
        )}
        {weeks.map((w, i) => {
          const current = i === weeks.length - 1;
          return (
            <div key={w.week_start} className="flex-1 h-full flex flex-col justify-end items-center gap-1 min-w-0">
              <span className="text-[10px] text-[var(--text-muted)] whitespace-nowrap">{w.earned + w.planned ? Math.round((w.earned + w.planned) / 100) / 10 + 'k' : ''}</span>
              <div className="w-full max-w-[42px] flex flex-col justify-end rounded-xl overflow-hidden" style={{ height: ((w.earned + w.planned) / max) * (H - 18) }}>
                {w.planned > 0 && <div style={{ height: `${(w.planned / (w.earned + w.planned)) * 100}%`, background: 'var(--track)' }} />}
                <div className="flex-1" style={{ background: current ? 'linear-gradient(180deg,#C9A84C,rgba(201,168,76,0.45))' : 'var(--chart-main)', opacity: current ? 1 : 0.8 }} />
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex gap-2 sm:gap-3">
        {weeks.map((w) => (
          <span key={w.week_start} className="flex-1 text-center text-[9.5px] sm:text-[10.5px] text-[var(--text-muted)] whitespace-nowrap overflow-hidden">
            <span className="sm:hidden">{weekLabel(w.week_start).split('–')[0]}</span>
            <span className="hidden sm:inline">{weekLabel(w.week_start)}</span>
          </span>
        ))}
      </div>
    </div>
  );
};

export const RevenuePage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/revenue/weekly?weeks=8`, { withCredentials: true })
      .then((r) => setData(r.data)).catch(() => setError(true));
  }, []);

  const cw = data?.current_week;
  const vsAvg = data && data.avg_week ? Math.round(((cw.earned + cw.planned) / data.avg_week - 1) * 100) : null;

  return (
    <TrainerShell>
      <main className="max-w-[1200px] px-4 sm:px-10 py-6 sm:py-10 flex flex-col gap-6" data-testid="revenue-page">
        <header className="flex flex-col gap-3">
          <p className="label-text text-[var(--text-muted)]">Finanse</p>
          <h1 className="big-num text-4xl sm:text-5xl">Przychód</h1>
          <p className="text-sm text-[var(--text-muted)] max-w-2xl">
            Wartość odbytych treningów. Trening z pakietu = cena pakietu ÷ liczba treningów (średnia),
            pojedynczy = cena za trening; sesja 2 h liczy się jako 2 treningi. Odwołane się nie liczą.
          </p>
        </header>

        {error && <Card className="p-6 text-sm text-[var(--text-muted)]">Nie udało się wczytać danych.</Card>}
        {!data && !error && <p className="text-[var(--text-muted)]">Ładowanie…</p>}

        {data && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Kpi testid="rev-week" label="Ten tydzień" value={zl(cw.earned)}
                sub={`${unitsLabel(cw.trainings_done)}${cw.planned ? ` · +${zl(cw.planned)} zaplanowane` : ''}`} />
              <Kpi testid="rev-avg" label="Średnio / tydzień" value={zl(data.avg_week)}
                sub={vsAvg == null ? 'za mało historii' : `ten tydzień ${vsAvg >= 0 ? '+' : ''}${vsAvg}% vs średnia`} />
              <Kpi testid="rev-month" label="Ten miesiąc" value={zl(data.month.earned)}
                sub={data.month.planned ? `+${zl(data.month.planned)} zaplanowane` : 'nic więcej zaplanowanego'} />
              <Kpi testid="rev-per-training" label="Średnio / trening" value={zl(data.avg_per_training)} sub="z odbytych treningów" />
            </div>

            <Card className="p-5 sm:p-8 flex flex-col gap-5">
              <div className="flex flex-wrap justify-between gap-3 items-baseline">
                <Label>Ostatnie 8 tygodni</Label>
                <span className="text-xs text-[var(--text-muted)]">jasny fragment = zaplanowane w tym tygodniu</span>
              </div>
              <WeekBars weeks={data.weeks} avg={data.avg_week} />
            </Card>

            {data.unpriced.length > 0 && (
              <Card className="p-5 sm:p-6 flex flex-col gap-2 border-[#C9A84C]/40" data-testid="rev-unpriced">
                <Label>Bez ceny — nie wliczone</Label>
                <p className="text-sm">
                  {data.unpriced.map((u) => `${u.name} (${unitsLabel(u.trainings)})`).join(', ')}.
                  {' '}Dodaj pakiet albo cenę za trening w karcie klienta, żeby te treningi weszły do przychodu.
                </p>
              </Card>
            )}

            <Card className="p-5 sm:p-8 flex flex-col gap-3">
              <Label>Ten tydzień · klienci</Label>
              {cw.by_client.length === 0 && <p className="text-sm text-[var(--text-muted)]">Brak treningów w tym tygodniu.</p>}
              {cw.by_client.map((c) => (
                <button key={c.client_id} type="button" onClick={() => navigate(`/client/${c.client_id}`)}
                  className="glass-soft rounded-2xl px-4 py-3 flex items-center justify-between gap-3 text-left">
                  <div className="min-w-0">
                    <p className="text-sm font-bold truncate">{c.name}</p>
                    <p className="text-xs text-[var(--text-muted)] truncate">
                      {unitsLabel(c.trainings)} · {c.rate != null ? `${zl(c.rate)} / trening · ${c.source}` : 'brak ceny'}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="big-num text-lg">{zl(c.revenue)}</p>
                    {c.planned > 0 && <p className="text-[11px] text-[var(--text-muted)]">+{zl(c.planned)} plan</p>}
                  </div>
                </button>
              ))}
            </Card>
          </>
        )}
      </main>
    </TrainerShell>
  );
};
